import React, { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  InputAdornment,
  IconButton
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import LockResetIcon from "@mui/icons-material/LockReset";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import {
  CognitoUser,
  CognitoUserPool
} from "amazon-cognito-identity-js";

import { getUsersByEmail, postUserNewPassword } from "src/general_helpers/UsersData";

export const RecuperarContrasenia = (props) => {
  const {
    setHeader,
    toggleModal,
    setSnackbarVisible,
    setMessage,
    setSeverity,
    aws_id_grupo_user,
    aws_id_cliente_aplicacion,
    setLoading,
  } = props;

  /* ================== STATE ================== */
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);

  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");

  const [usuarioEmailExist, setUsuarioEmailExist] = useState("");

  /* ================== COGNITO ================== */
  const userPool = new CognitoUserPool({
    UserPoolId: aws_id_grupo_user,
    ClientId: aws_id_cliente_aplicacion
  });

  /* ================== VALIDACIONES ================== */
  const validatePasswordPolicy = (password) => {
    if (password.length < 8) return "Debe tener al menos 8 caracteres";
    if (!/[A-Z]/.test(password)) return "Debe tener al menos una mayúscula";
    if (!/[0-9]/.test(password)) return "Debe tener al menos un número";
    return "";
  };

  /* ================== HANDLERS ================== */
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailError(!emailPattern.test(value));
  };

  const handlePasswordChange = (value) => {
    setNewPassword(value);
    const error = validatePasswordPolicy(value);
    setPasswordError(error);

    if (confirmPassword && value !== confirmPassword) {
      setConfirmError("Las contraseñas no coinciden");
    } else {
      setConfirmError("");
    }
  };

  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    if (value !== newPassword) {
      setConfirmError("Las contraseñas no coinciden");
    } else {
      setConfirmError("");
    }
  };

  /* ---------- PASO 1 ---------- */
  const handleSendCode = async () => {
    setLoading(true);

    if (!email || emailError) {
      setLoading(false);
      setEmailError(true);
      return;
    }

    const emailExists = await getUsersByEmail(email);
    if (!emailExists) {
      setLoading(false);
      setSeverity("warning");
      setMessage("El correo no está registrado");
      setSnackbarVisible(true);
      return;
    }

    setUsuarioEmailExist(emailExists);
    await setHeader();

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    cognitoUser.forgotPassword({
      onSuccess() {
        setLoading(false);
        setSeverity("success");
        setMessage("Te enviamos un código por correo");
        setSnackbarVisible(true);
        setStep(2);
      },
      onFailure(err) {
        setLoading(false);
        setSeverity("error");
        setMessage(err.message || "Error al enviar el correo");
        setSnackbarVisible(true);
      }
    });
  };

  /* ---------- PASO 2 ---------- */
  const handleConfirmPassword = async () => {
    setLoading(true);

    if (!code || !newPassword || !confirmPassword) {
      setLoading(false);
      setSeverity("warning");
      setMessage("Completa todos los campos");
      setSnackbarVisible(true);
      return;
    }

    if (passwordError || confirmError) {
      setLoading(false);
      setSeverity("warning");
      setMessage("Revisá los errores de contraseña");
      setSnackbarVisible(true);
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    const responseUser = await postUserNewPassword(usuarioEmailExist, newPassword);
    if (responseUser.status !== true) {
      setLoading(false);
      setSeverity("error");
      setMessage("Error al actualizar la contraseña en el sistema");
      setSnackbarVisible(true);
      return;
    }

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess() {
        setLoading(false);
        setSeverity("success");
        setMessage("Contraseña actualizada correctamente");
        setSnackbarVisible(true);
        toggleModal();
      },
      onFailure(err) {
        setLoading(false);
        setSeverity("error");
        setMessage(err.message || "Código inválido");
        setSnackbarVisible(true);
      }
    });
  };

  /* ================== UI ================== */
  return (
    <Grid container spacing={2} justifyContent="center">

      {step === 1 && (
        <>
          <Grid item xs={12}>
            <Typography sx={{ fontSize: 16, color: "grey" }}>
              Ingresá tu correo electrónico
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Correo electrónico"
              fullWidth
              value={email}
              onChange={handleEmailChange}
              error={emailError}
              helperText={emailError ? "Correo inválido" : ""}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="success"
              onClick={handleSendCode}
              endIcon={<SendIcon />}
            >
              Enviar código
            </Button>
          </Grid>
        </>
      )}

      {step === 2 && (
        <>
          <Grid item xs={12}>
            <Typography sx={{ fontSize: 16, color: "grey" }}>
              Ingresá el código recibido y tu nueva contraseña
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Código"
              fullWidth
              value={code}
              autoComplete="one-time-code"
              onChange={(e) => setCode(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nueva contraseña"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={newPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
              error={!!passwordError}
              helperText={passwordError || "Mínimo 8 caracteres, una mayúscula y un número"}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Confirmar contraseña"
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              error={!!confirmError}
              helperText={confirmError}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="success"
              onClick={handleConfirmPassword}
              endIcon={<LockResetIcon />}
            >
              Cambiar contraseña
            </Button>
          </Grid>
        </>
      )}

    </Grid>
  );
};

export default RecuperarContrasenia;
